/**
 * What stage 3 reads besides links (1.J deliverables 24 and 30): the flags, NOW with the
 * calendar, and each shown page's placements under 1.O-1's key (one request per target, shared
 * with the When pill and the Chronology tab). Every read is off while its flag is off.
 */
import { flagsFromMe, type PlacementV1, placementsList } from '@codex/contracts';
import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';
import { api } from '../auth/client.js';
import { useShellSession } from '../shell/session.js';
import {
  useWhenContext,
  useWhenEvents,
  type WhenContextValue,
  whenKeys,
} from '../when/calendar-context.js';

export interface LinkFlags {
  readonly shell: boolean;
  readonly chrono: boolean;
  readonly ledger: boolean;
}

export function useLinkFlags(): LinkFlags {
  const me = useShellSession();
  const flags = me === null ? null : flagsFromMe(me);
  return {
    shell: flags?.shell === true,
    chrono: flags?.shell === true && flags.chrono === true,
    ledger: flags?.shell === true && flags.ledger === true,
  };
}

/** The calendar and NOW; sends nothing, and mounts no subscription, while `chrono` is off. */
export function useLinkWhen(chrono: boolean): WhenContextValue {
  const ctx = useWhenContext(chrono);
  useWhenEvents(null, chrono);
  return ctx;
}

/** `placements.list` of each id, by page; an id not yet read is absent. */
export function useTargetPlacements(
  ids: readonly string[],
  enabled: boolean,
): Map<string, PlacementV1[]> {
  const distinct = useMemo(() => [...new Set(ids)], [ids]);
  const results = useQueries({
    queries: distinct.map((id) => ({
      queryKey: whenKeys.placements(id),
      queryFn: async (): Promise<PlacementV1[]> =>
        (await api(placementsList, { params: { id }, query: {} })).items,
      enabled,
      retry: false,
    })),
  });
  const map = new Map<string, PlacementV1[]>();
  results.forEach((result, index) => {
    const id = distinct[index];
    if (id !== undefined && result.data) map.set(id, result.data);
  });
  return map;
}
